/**
 * Mintytheme Configuration - Minty color theme for the application
 *
 * MUI theme configuration with custom color palette
 * Following ReactJS standards with functional components and hooks
 */

import { createTheme } from '@mui/material/styles';
import type { AppTheme } from '@/types/theme';
import { alpha } from '@mui/material';

/**
 * Create base theme options common to light and dark themes
 *
 * @returns Base theme options for both light and dark themes
 */
const baseThemeOptions = {
  breakpoints: {
    values: {
      xs: 0, // phones
      sm: 600, // tablets
      md: 900, // small laptops
      lg: 1200,
      xl: 1536,
    },
  },

  spacing: 8,

  typography: {
    fontFamily: `'Inter', system-ui, sans-serif`,
    button: {
      textTransform: 'none',
    },
    h4: {
      fontWeight: 800,
      letterSpacing: 0.4,
    },
  },

  components: {
    MuiContainer: {
      defaultProps: {
        maxWidth: 'md', // ⬅️ MASSIVE mobile win
      },
    },
  },
} as const;

/**
 * Create Mintytheme for light mode
 *
 * @returns MUI theme configured for light mode with Mintytheme colors
 */
export const createLightTheme = (): AppTheme => {
  return createTheme({
    ...baseThemeOptions,
    palette: {
      mode: 'light',
      background: {
        default: '#f6f4ef',
        paper: '#fbfaf7',
      },
      primary: {
        main: '#2f7f6f', // darker mint for light mode
      },
      secondary: {
        main: '#c7656a', // darker salmon
      },
      text: {
        primary: '#1d1d1d',
        secondary: '#444',
      },
      divider: '#2a2a2a',
    },

    shape: {
      borderRadius: 6,
    },

    components: {
      MuiPaper: {
        styleOverrides: {
          root: {
            border: '3px solid #2a2a2a',
            boxShadow: 'none',
          },
        },
      },

      MuiButton: {
        styleOverrides: {
          root: ({ theme, ownerState }) => {
            const color = ownerState.color ?? 'primary';

            // Define only the keys that exist in palette
            type PaletteKey = 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
            const paletteColors: PaletteKey[] = [
              'primary',
              'secondary',
              'error',
              'info',
              'success',
              'warning',
            ];

            // Narrow color safely
            const safeColor: PaletteKey = paletteColors.includes(color as PaletteKey)
              ? (color as PaletteKey)
              : 'primary';

            const paletteColor = theme.palette[safeColor];

            return {
              textTransform: 'none',
              fontWeight: 700,
              border: '3px solid #2a2a2a',
              boxShadow: '2px 2px 0 #2a2a2a',

              //this looks better on an action button that is the primary green colour, like a Save button
              // '&:hover': {
              //   backgroundColor: '#78c2ad',
              //   border: '3px solid #2a2a2a',
              //   boxShadow: '2px 2px 0 #2a2a2a',
              // },

              //this looks good on a white button and a suttle border change, like a sign in button
              '&:hover': {
                borderColor: paletteColor.main,
                boxShadow: `2px 2px 0 ${paletteColor.main}`,
                backgroundColor: alpha(paletteColor.main, 0.3), // dynamic hover based on color
              },
            };
          },
        },
      },

      MuiDivider: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderColor: theme.palette.divider,
            borderWidth: 2,
          }),
        },
      },

      MuiCard: {
        styleOverrides: {
          root: {
            border: '3px solid #2a2a2a',
          },
        },
      },
    },
  }) as AppTheme;
};

/**
 * Create Mintytheme for dark mode
 *
 * @returns MUI theme configured for dark mode with Mintytheme colors
 */
export const createDarkTheme = (): AppTheme => {
  return createTheme({
    ...baseThemeOptions,
    palette: {
      mode: 'dark',
      background: {
        default: '#1b2128', // app background
        paper: '#222a33', // cards, dialogs
      },
      primary: {
        main: '#78c2ad',
      },
      secondary: {
        main: '#f3969a',
      },
      text: {
        primary: '#e6edf3',
        secondary: '#b7c0cc',
      },
      divider: '#4b5563',
    },

    shape: {
      borderRadius: 6,
    },

    components: {
      MuiPaper: {
        styleOverrides: {
          root: ({ theme }) => ({
            backgroundImage: 'none',
            border: `3px solid ${theme.palette.divider}`,
          }),
        },
      },

      MuiButton: {
        styleOverrides: {
          root: ({ theme, ownerState }) => {
            const color = ownerState.color ?? 'primary';

            // Define only the keys that exist in palette
            type PaletteKey = 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
            const paletteColors: PaletteKey[] = [
              'primary',
              'secondary',
              'error',
              'info',
              'success',
              'warning',
            ];

            // Narrow color safely
            const safeColor: PaletteKey = paletteColors.includes(color as PaletteKey)
              ? (color as PaletteKey)
              : 'primary';

            const paletteColor = theme.palette[safeColor];

            return {
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: 8,
              border: `3px solid ${theme.palette.divider}`,
              boxShadow: `2px 2px 0 ${theme.palette.divider}`,

              //this looks better on an action button that is the primary green colour, like a Save button
              // '&:hover': {
              //   backgroundColor: '#78c2ad',
              //   border: '3px solid #2a2a2a',
              //   boxShadow: '2px 2px 0 #2a2a2a',
              // },

              //this looks good on a white button and a suttle border change, like a sign in button
              '&:hover': {
                borderColor: paletteColor.main,
                boxShadow: `2px 2px 0 ${paletteColor.main}`,
                backgroundColor: alpha(paletteColor.main, 0.3), // dynamic hover based on color
              },
            };
          },
        },
      },

      MuiDivider: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderColor: theme.palette.divider,
            borderWidth: 2,
          }),
        },
      },

      MuiTableCell: {
        styleOverrides: {
          head: ({ theme }) => ({
            fontWeight: 800,
            borderBottom: `3px solid ${theme.palette.divider}`,
          }),

          body: ({ theme }) => ({
            borderBottom: `2px solid ${theme.palette.divider}`,
          }),
        },
      },

      /* ===============================
       * Tooltips (important for charts)
       * =============================== */
      MuiTooltip: {
        styleOverrides: {
          tooltip: ({ theme }) => ({
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            border: `2px solid ${theme.palette.divider}`,
            boxShadow: 'none',
            fontSize: '0.75rem',
          }),
        },
      },

      MuiCard: {
        styleOverrides: {
          root: ({ theme }) => ({
            border: `3px solid ${theme.palette.divider}`,
          }),
        },
      },
    },
  }) as AppTheme;
};
